import { useEffect, useId, useRef, useState } from "react";
import { BrainCircuit, ChevronDown, ShieldAlert, X } from "lucide-react";
import { RCA_LIST_RESULTS_LIMIT } from "@/constants/rca-constants.js";
import { isRequestAborted, runLLMStream } from "@/api/rca-api.js";
import { Button } from "@/components/ui/button.jsx";
import { LoadingSkeleton, LoadingSpinner } from "@/components/ui/loading.jsx";
import { buildRcaAiRequest } from "@/components/root-cause-analysis/rca-ai-request-builder.js";
import RcaAiAnalysisMarkdown from "@/components/root-cause-analysis/rca-ai-analysis-markdown.jsx";
import { randomUUID } from "@/lib/random-uuid.js";
import RcaAiAnalysisFeedback from "@/components/root-cause-analysis/rca-ai-analysis-feedback.jsx";
import RcaAiAnalysisActions from "@/components/root-cause-analysis/rca-ai-analysis-actions.jsx";
import { cn } from "@/lib/utils.js";

const AI_ANALYSIS_HEADER_SUBTEXT =
  "Analyzes 7 days of alarms, 30 days of shutdown history, and 6–12 hours of sensor data to explain what happened and recommend next steps.";

const AI_ANALYSIS_ACCESS_DENIED_SUBTEXT =
  "AI shutdown analysis is not available for your account — contact your account manager to request access.";

const AI_ANALYSIS_ACCESS_LOADING_SUBTEXT =
  "Checking AI shutdown analysis availability for your account…";

const EXPLAIN_BUTTON_MIN_WIDTH = "min-w-[12.5rem]";

/**
 * Shown while the API prepares context before the first streamed token arrives.
 */
function RcaAiAnalysisPending() {
  return (
    <div className='space-y-5' aria-busy='true'>
      <div className='flex items-center gap-3'>
        <div className='relative flex h-11 w-11 shrink-0 items-center justify-center'>
          <span
            className='absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-cyan/30 opacity-50'
            aria-hidden
          />
          <span
            className='absolute inline-flex h-8 w-8 animate-pulse rounded-full bg-brand-cyan/20'
            aria-hidden
          />
          <BrainCircuit
            className='relative h-5 w-5 text-brand-cyan'
            aria-hidden
          />
        </div>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-foreground'>
            Preparing analysis…
          </p>
          <p className='text-xs text-muted-foreground'>
            Reviewing alarms, shutdown history, and sensor trends
          </p>
        </div>
      </div>

      <div className='space-y-3'>
        <LoadingSkeleton className='h-3 w-32 bg-brand-cyan/20' />
        <div className='space-y-2'>
          <LoadingSkeleton className='h-3 w-full bg-brand-cyan/20' />
          <LoadingSkeleton className='h-3 w-[92%] bg-brand-cyan/20' />
          <LoadingSkeleton className='h-3 w-[78%] bg-brand-cyan/20' />
        </div>
        <LoadingSkeleton className='h-3 w-28 bg-brand-cyan/20' />
        <div className='space-y-2'>
          <LoadingSkeleton className='h-3 w-full bg-brand-cyan/20' />
          <LoadingSkeleton className='h-3 w-[85%] bg-brand-cyan/20' />
        </div>
      </div>
    </div>
  );
}

/**
 * LLM-powered shutdown analysis on Root Cause Analysis.
 *
 * @param {{
 *   canUseAiAnalysis?: boolean,
 *   isAccessLoading?: boolean,
 *   defaultExpanded?: boolean,
 *   downtimeEventId?: number|null,
 *   startDate?: string|null,
 *   assetId?: string|number|null,
 *   assetNumber?: string|null,
 *   measurementRows?: import("@/components/root-cause-analysis/rca-measurement-trend-shared.jsx").MeasurementWithStatsRow[],
 *   trendWindowHours?: number,
 *   measurementsLoading?: boolean,
 *   hasShutdownSelected?: boolean,
 * }} props
 */
export default function RcaAiAnalysis({
  canUseAiAnalysis = false,
  isAccessLoading = false,
  defaultExpanded = true,
  downtimeEventId = null,
  startDate = null,
  assetId = null,
  assetNumber = null,
  measurementRows = [],
  trendWindowHours = 6,
  measurementsLoading = false,
  hasShutdownSelected = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(
    /** @type {string|null} */ (null)
  );
  const [analysisResult, setAnalysisResult] = useState(
    /** @type {string|null} */ (null)
  );
  const [analysisRunId, setAnalysisRunId] = useState(
    /** @type {string|null} */ (null)
  );
  const [isRiskBannerDismissed, setIsRiskBannerDismissed] = useState(false);
  const headingId = useId();
  const panelId = useId();
  const streamAbortRef = useRef(/** @type {AbortController|null} */ (null));
  const markdownContentRef = useRef(/** @type {HTMLDivElement|null} */ (null));

  // Step outside React state for the delta. For performance and smoothness we don't want to render a bunch of components on every delta that comes in.
  // So we accumulate deltas in this ref, but we only apply them relatively occasionally with the scheduleDeltaFlush function - no more than once per frame.
  const pendingDeltaRef = useRef("");
  const flushScheduledRef = useRef(false);

  useEffect(() => {
    streamAbortRef.current?.abort();
    setAnalysisError(null);
    setAnalysisResult(null);
    setAnalysisRunId(null);
    setIsRiskBannerDismissed(false);
  }, [downtimeEventId, startDate, trendWindowHours]);

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  function flushPendingDelta() {
    // Update the state from the delta all in one go.
    flushScheduledRef.current = false;
    const pending = pendingDeltaRef.current;
    if (!pending) {
      return;
    }

    pendingDeltaRef.current = "";
    setAnalysisResult((prev) => (prev ?? "") + pending);
  }

  function scheduleDeltaFlush() {
    if (flushScheduledRef.current) {
      return;
    }

    flushScheduledRef.current = true;
    requestAnimationFrame(flushPendingDelta);
  }

  function toggleExpanded() {
    setExpanded((v) => !v);
  }

  const headerSubtext = isAccessLoading
    ? AI_ANALYSIS_ACCESS_LOADING_SUBTEXT
    : canUseAiAnalysis
      ? AI_ANALYSIS_HEADER_SUBTEXT
      : AI_ANALYSIS_ACCESS_DENIED_SUBTEXT;

  const hasMeasurementRows = measurementRows.length > 0;

  const hasCompletedAnalysis = analysisRunId != null;

  const explainDisabled =
    !hasShutdownSelected
    || downtimeEventId == null
    || startDate == null
    || assetId == null
    || measurementsLoading
    || !hasMeasurementRows
    || isAnalyzing
    || hasCompletedAnalysis;

  const explainDisabledReason = !hasShutdownSelected
    ? "Select a shutdown event first."
    : measurementsLoading
      ? "Measurements are still loading."
      : !hasMeasurementRows
        ? "No measurement trends are available for this shutdown."
        : null;

  async function handleExplainShutdown() {
    if (explainDisabled) return;

    // Make sure there's no still running requests due to user refreshes or some such before we start
    streamAbortRef.current?.abort();
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);
    setAnalysisRunId(null);
    setIsRiskBannerDismissed(false);
    pendingDeltaRef.current = "";
    flushScheduledRef.current = false;

    try {
      const request = buildRcaAiRequest({
        downtimeEventId,
        startDate,
        requestId: randomUUID(),
        assetId,
        trendWindowHours,
        alarmAndDowntimeLimit: RCA_LIST_RESULTS_LIMIT,
        rows: measurementRows,
      });

      await runLLMStream(request, {
        signal: abortController.signal,
        onDelta: (text) => {
          pendingDeltaRef.current += text;
          scheduleDeltaFlush();
        },
        onDone: ({ id, words }) => {
          // Use the final response to show the pieced together result cleanly.
          pendingDeltaRef.current = "";
          flushScheduledRef.current = false;
          setAnalysisRunId(id);
          setAnalysisResult(words);
        },
      });
    } catch (err) {
      if (isRequestAborted(err)) {
        return;
      }

      console.error("runLLMStream failed:", err);
      setAnalysisError("Failed to analyze this shutdown. Please try again.");
    } finally {
      if (streamAbortRef.current === abortController) {
        streamAbortRef.current = null;
      }
      setIsAnalyzing(false);
    }
  }

  return (
    <section
      className={cn(
        "rounded-lg border border-brand-cyan/35 bg-brand-cyan/10 px-3 py-3 shadow-sm sm:px-4",
        "ring-1 ring-brand-cyan/20 dark:border-brand-cyan/40 dark:bg-brand-cyan/10 dark:ring-brand-cyan/25"
      )}>
      <div className='flex w-full flex-wrap items-start gap-x-2 gap-y-2 sm:flex-nowrap sm:items-center'>
        <button
          type='button'
          id={headingId}
          className='flex min-w-0 flex-1 items-start gap-3 rounded-md text-left transition-colors hover:bg-brand-cyan/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/60 dark:hover:bg-brand-cyan/20'
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}>
          <BrainCircuit
            className='mt-0.5 h-5 w-5 shrink-0 text-brand-cyan'
            aria-hidden
          />
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
              <h3 className='text-sm font-semibold text-foreground'>
                AI Shutdown Analysis
              </h3>
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>{headerSubtext}</p>
          </div>
        </button>
        <button
          type='button'
          className='ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-brand-cyan transition-colors hover:bg-brand-cyan/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/60 dark:hover:bg-brand-cyan/20 sm:ml-0'
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}
          aria-label={
            expanded
              ? "Collapse AI shutdown analysis"
              : "Expand AI shutdown analysis"
          }>
          <ChevronDown
            className={cn(
              "h-6 w-6 transition-transform",
              expanded && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </div>

      {expanded ? (
        <div
          id={panelId}
          role='region'
          aria-labelledby={headingId}
          className='mt-3 border-t border-brand-cyan/30 pt-3 dark:border-brand-cyan/35'>
          {isAccessLoading ? (
            <div
              className='mx-auto flex max-w-2xl flex-col items-center gap-2 py-2 text-center'
              aria-busy='true'
              aria-live='polite'>
              <LoadingSpinner className='h-5 w-5 border-brand-cyan border-b-transparent' />
              <p className='text-xs text-muted-foreground'>
                Checking availability for your account…
              </p>
            </div>
          ) : canUseAiAnalysis ? (
            <div className='space-y-3'>
              <div className='mx-auto max-w-2xl space-y-3 text-center'>
                <p className='text-sm font-medium text-foreground'>
                  AI-Powered Root Cause Analysis
                </p>
                {!hasCompletedAnalysis ? (
                  <div className='flex flex-col items-center gap-2'>
                    <Button
                      type='button'
                      size='sm'
                      disabled={explainDisabled}
                      onClick={handleExplainShutdown}
                      className={cn(
                        EXPLAIN_BUTTON_MIN_WIDTH,
                        "border-0 bg-primary text-primary-foreground shadow-none hover:bg-primary/90",
                        explainDisabled
                          && "disabled:pointer-events-none disabled:!bg-muted disabled:!text-muted-foreground disabled:hover:!bg-muted"
                      )}>
                      {isAnalyzing ? (
                        <span className='inline-flex w-full items-center justify-center gap-2'>
                          <span
                            className='inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent'
                            aria-hidden
                          />
                          Analyzing…
                        </span>
                      ) : (
                        "Explain this shutdown"
                      )}
                    </Button>
                    {explainDisabledReason ? (
                      <p className='text-xs text-muted-foreground'>
                        {explainDisabledReason}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {analysisError ? (
                  <p
                    className='text-sm text-red-600 dark:text-red-400'
                    role='alert'>
                    {analysisError}
                  </p>
                ) : null}
              </div>
              {analysisResult || isAnalyzing ? (
                <div className='mx-auto w-[80%] min-w-0 space-y-3'>
                  <div
                    className='rounded-md border border-border bg-card px-4 py-4 text-left shadow-sm'
                    aria-live='polite'
                    aria-busy={isAnalyzing}>
                    {analysisResult ? (
                      <>
                        {assetNumber && startDate && !isAnalyzing ? (
                          <RcaAiAnalysisActions
                            contentRef={markdownContentRef}
                            assetNumber={assetNumber}
                            startDate={startDate}
                            className='mb-3'
                          />
                        ) : null}
                        <RcaAiAnalysisMarkdown
                          ref={markdownContentRef}
                          markdown={analysisResult}
                        />
                        {!isAnalyzing && analysisRunId ? (
                          <RcaAiAnalysisFeedback
                            analysisRunId={analysisRunId}
                          />
                        ) : null}
                      </>
                    ) : (
                      <RcaAiAnalysisPending />
                    )}
                  </div>
                  {analysisResult && !isAnalyzing && !isRiskBannerDismissed ? (
                    <div
                      className='rounded-md border border-orange-500/70 bg-orange-500/10 px-3 py-2.5 text-left shadow-sm dark:border-orange-400/60 dark:bg-orange-500/15'
                      role='note'
                      aria-live='polite'>
                      <div className='flex items-start justify-between gap-3'>
                        <div className='min-w-0 space-y-1.5 text-xs text-orange-950 dark:text-orange-100'>
                          <div className='flex items-start gap-2'>
                            <ShieldAlert
                              className='mt-0.5 h-4 w-4 shrink-0 text-orange-700 dark:text-orange-400'
                              aria-hidden
                            />
                            <p className='font-semibold'>
                              AI-Generated Analysis — Verify Before Acting
                            </p>
                          </div>
                          <p className='pl-6 text-orange-900/90 dark:text-orange-100/90'>
                            This root cause analysis is produced by a large
                            language model from telemetry, alarm history, and
                            prior shutdown records. It is a decision-support
                            aid, not an authoritative diagnosis, and may contain
                            errors or omit factors outside the available data.
                            Confirm all findings against physical inspection,
                            OEM guidance, and qualified engineering judgment
                            before performing maintenance or operational
                            changes. Detechtion does not warrant the accuracy of
                            generated content and accepts no liability for
                            actions taken solely in reliance on it.
                          </p>
                        </div>
                        <button
                          type='button'
                          className='-mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-orange-800 transition-colors hover:bg-orange-500/20 hover:text-orange-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600/60 dark:text-orange-200 dark:hover:bg-orange-500/25 dark:hover:text-orange-50'
                          onClick={() => setIsRiskBannerDismissed(true)}
                          aria-label='Dismiss AI generated content warning'>
                          <X className='h-3.5 w-3.5' aria-hidden />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <div className='mx-auto max-w-2xl space-y-2 text-center text-xs leading-relaxed text-muted-foreground'>
              <p>
                This feature uses an LLM to review recent alarms, shutdown
                history, and pre-shutdown sensor trends to explain what happened
                and suggest next steps. Contact your account manager to request
                access.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
